import { atom, map } from 'nanostores';
import type { ElementContext } from '../preview-bridge/elementContext';
import type { ComputedStylePayload, ElementDebugSource } from '../preview-bridge/protocol';
import type { HotEditListContext } from '../preview-bridge/listContext';
import type { HotEditElementKind } from '../preview-bridge/elementKind';
import type { HotEditPropSection } from '../preview-bridge/elementKind';

export type HotEditBridgeApi = {
  applyInstantCss: (elementId: string, property: string, value: string) => void;
  applyInstantText: (elementId: string, text: string) => void;
  /** Debounced RAF-like instant text preview for inputs that re-render rapidly */
  scheduleApplyInstantText: (elementId: string, text: string) => void;
  applyInstantDom: (elementId: string, key: string, value: string) => void;
  /** A record's cell in the preview, before the data file is written on Apply. */
  applyInstantData: (
    rowKey: string,
    oldValue: string,
    newValue: string,
    cellIndex?: number,
  ) => void;
  /** A record added, copied, removed or moved, in the preview, before Apply. */
  applyInstantRow: (row: {
    op: 'add' | 'duplicate' | 'remove' | 'move';
    rowKey: string;
    newKey?: string;
    toIndex?: number;
    values?: { from: string; to: string }[];
  }) => void;
  bumpMergedFromTailwind: (property: string, value: string) => void;
  /** Hide the selected element, or bring it back exactly as it was. */
  toggleHidden: (hide: boolean) => void;
  queuePersistTypographyLayout: () => void;
  queuePersistText: (text: string) => void;
  queuePersistPlaceholder: (placeholder: string) => void;
  queuePersistDomValue: (value: string) => void;
  queuePersistSelectOptions: (options: string[]) => void;
  queuePersistInputType: (type: string) => void;
  openSelectionInEditor: () => void;
  exitHotEditMode: () => void;
  /** Select an element by its preview id (a breadcrumb step). */
  selectElement: (elementId: string) => void;
  /** Select the parent of the selection (arrow-up). */
  selectParent: () => void;
  /** Clear the selection (Esc). */
  deselect: () => void;
  /** Select the list row whose React key is `key` (a data-grid row). */
  selectByKey: (key: string) => void;
  /** Run the change that failed to save again. */
  retryLastSave: () => void;
  /** Write every pending change, in one pass. */
  applyPendingEdits: () => Promise<boolean>;
  /** Throw the pending changes away and put the preview back. */
  discardPendingEdits: () => void;
  /** Take back the last pending change; false when there was none. */
  undoPendingEdit: () => boolean;
  /** Show a design system colour in the whole preview at once, before Apply writes it. */
  previewThemeToken: (token: string, value: string | null) => void;
  /** Queue the design system colour itself, rather than a colour on one element. */
  queueThemeToken: (token: string, value: string, label: string) => void;
  /** Put the selected element on the chat composer as a chip, with a picture of it. */
  askIdeoz: () => void;
  /** Draw a different Lucide icon here; queued for Apply like any other change. */
  swapIcon: (iconName: string, markup?: string) => void;
  /** Show another picture in the selected <img> now, and queue the change for Apply. */
  replaceImage: (args: { storePath: string; reference: string; dataUri: string }) => void;
};

/** Kept in step with `EDIT_MARK_KINDS` in chef's `editMarks.ts`. */
export type EditMark = 'text' | 'surface' | 'list' | 'record' | 'image';

export const EDIT_MARKS: readonly EditMark[] = ['text', 'surface', 'list', 'record', 'image'];

export type HotEditSelection = {
  elementId: string;
  tagName: string;
  ownerComponentName?: string;
  source?: ElementDebugSource | null;
  implementationSource?: ElementDebugSource | null;
  /** DOM path segments for nth-of-type / debugging */
  selectorPath?: string;
  /** Repeated list instance (.map loops) — from preview fiber + DOM */
  listContext?: HotEditListContext | null;
  computedStyles: ComputedStylePayload;
  textContent: string;
  className: string;
  hasChildElements: boolean;
  elementKind?: HotEditElementKind;
  editTargetKind?: 'callSite' | 'designSystemOnly' | 'missing';
  canPersistClasses?: boolean;
  blockReason?: string | null;
  inputPlaceholder?: string;
  inputValue?: string;
  inputType?: string;
  optionValues?: string[];
  /** Derived on host — sections rendered for this selection */
  editableSections?: HotEditPropSection[];
  /**
   * The repeated row this element belongs to: its record key, place, how many rows share the
   * template, and whether this element is the row's outermost one. Null when not repeated.
   */
  listRow?: { key: string | null; index: number; count: number; isRowRoot: boolean } | null;
  /** Set when the element itself holds a repeated list. */
  listContainer?: { count: number; firstKey: string | null } | null;
  /** What Chef marked this element as while it wrote the prototype (`editMarks.ts` in chef). */
  editMark?: EditMark | null;
  /** The prototype carries edit marks; without them the panel falls back to its own rules. */
  hasEditMarks?: boolean;
  /** The page frame (shell, sidebar, top bar), which the panel does not edit. */
  isShell?: boolean;
  /** How many elements this one holds, for containers. */
  childCount?: number;
  /** Component chain from the page down to the element (breadcrumb) */
  breadcrumb?: { label: string; elementId: string }[];
  /** The prototype's CSS color variables, for design-system swatches */
  themeTokens?: Record<string, string>;
  /** An <img>'s source as the preview shows it: a data: URI when it is a stored asset. */
  imageSrc?: string;
  /** The founder hid this element; it shows dimmed while editing and Hide becomes Unhide. */
  isHidden?: boolean;
};

/** Claude-style inspect + Tweaks drawer mode */
export const hotEditStore = {
  mode: atom<boolean>(false),
  selection: atom<HotEditSelection | null>(null),
  iframePathname: atom<string>('/'),
  /** Wired by {@link packages/workbench/components/Workbench} when iframe bridge is mounted */
  bridgeApi: atom<HotEditBridgeApi | null>(null),
  /** The selection's box inside the preview, kept up to date as it scrolls (toolbar placement). */
  selectionRect: atom<{ top: number; left: number; width: number; height: number } | null>(null),
  /**
   * In a list row: `item` edits that record's content, `all` styles the shared row template
   * (hot edit plan, phase 3).
   */
  listScope: atom<'item' | 'all'>('item'),
  /** The data grid is open on the list holding this record. */
  dataGrid: atom<{ recordId: string } | null>(null),
  /**
   * Whether the founder's last change reached the prototype's files. The panel shows it where the
   * change was made, and offers Retry when it failed.
   */
  save: atom<'idle' | 'pending' | 'saving' | 'saved' | 'failed'>('idle'),
  /**
   * The question the editor is asking about the change just made - whether a colour is this
   * element's or the design system's, whether a change to one row is meant for all of them
   * (Prototype Editor UX plan, step 4). One at a time, beside the toolbar.
   */
  smartPrompt: atom<SmartPromptState | null>(null),
  /** Answers the founder has already given, so the same question is asked once. */
  smartAnswers: map<{ repeatScope?: 'item' | 'all' }>({}),
  /** Something to know about the change being made, said once and then never again. */
  hint: atom<{ id: string; text: string } | null>(null),
  /** The element on the composer, until the founder sends their message or takes it off. */
  pendingAsk: atom<PendingAsk | null>(null),
  /** How tall the founder dragged the records sheet, in px; kept for the session. */
  sheetHeight: atom<number>(260),
};

/** Questions waiting their turn: the card shows one at a time (Editor Glass Pass plan). */
const smartPromptQueue: SmartPromptState[] = [];

/** Ask the founder something, after whatever they are already being asked. */
export function askSmartPrompt(prompt: SmartPromptState): void {
  if (hotEditStore.smartPrompt.get()) smartPromptQueue.push(prompt);
  else hotEditStore.smartPrompt.set(prompt);
}

/** The card was answered or dismissed; the next question, if any, takes its place. */
export function nextSmartPrompt(): void {
  hotEditStore.smartPrompt.set(smartPromptQueue.shift() ?? null);
}

const HINTS_SEEN_KEY = 'ideoz.hotEdit.hintsSeen';

function hintsSeen(): string[] {
  try {
    const raw = window.localStorage.getItem(HINTS_SEEN_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    // Private windows and blocked storage: the hint shows again, which is the safe way to be wrong.
    return [];
  }
}

/**
 * Something the founder should know about the change they are making, said once.
 *
 * Not a question - there is nothing to answer - and not a message after the fact, which is where
 * this used to be: a toast after Apply, about a change already written. It appears with the
 * change, and "Got it" is the end of it for good.
 */
export function showHint(id: string, text: string): void {
  if (hintsSeen().includes(id)) return;
  hotEditStore.hint.set({ id, text });
}

/** Put it away. `forGood` is the founder saying they have read it. */
export function dismissHint(forGood: boolean): void {
  const hint = hotEditStore.hint.get();
  hotEditStore.hint.set(null);
  if (!hint || !forGood) return;
  try {
    const seen = hintsSeen();
    if (!seen.includes(hint.id)) {
      window.localStorage.setItem(HINTS_SEEN_KEY, JSON.stringify([...seen, hint.id]));
    }
  } catch {
    /* nothing to remember it in; it will be said again */
  }
}

/** How much of the element's own words the chip and the message show after its name. */
const ASK_WORDS_MAX = 24;

/**
 * What the founder picked, in words: its name and a few of its own, as the chip says it. The
 * message they send keeps the same line, so the conversation reads the way the composer did.
 */
export function askLabel(ask: PendingAsk): string {
  const words = ask.context.text?.trim();
  if (!words) return ask.label;
  const shown = words.length > ASK_WORDS_MAX ? `${words.slice(0, ASK_WORDS_MAX - 1)}…` : words;
  return `${ask.label} · ${shown}`;
}

/** The picture is only worth sending while it is small enough not to slow the turn down. */
const MAX_SCREENSHOT_CHARS = 500_000;

/**
 * The one the last send took, and when. One send is read twice - the message the founder sees
 * keeps the element, and the request to the builder carries it. The builder's request only goes
 * out once Nexus has answered, often a minute later, so the element waits for it here.
 */
let lastTaken: { ask: PendingAsk; at: number } | null = null;
/**
 * How long a send's element waits for its build. Nexus answers within a few minutes; a build that
 * starts later than this is not the one the founder asked about.
 */
const SAME_TURN_MS = 10 * 60_000;

/**
 * Apply writes the files and the preview compiles again, which mounts a new page. That reload is
 * the founder's own change landing, not them leaving the editor - but it used to put them out of
 * edit mode every time, so every Apply cost them their place and another click on Edit. A reload
 * that follows an Apply is held; any other one still ends the session.
 */
let keepModeUntil = 0;
const KEEP_MODE_MS = 15_000;

export function holdEditModeThroughReload(): void {
  keepModeUntil = Date.now() + KEEP_MODE_MS;
}

/** True once for a reload that an Apply asked for; the hold is spent either way. */
export function takeEditModeHold(): boolean {
  const held = keepModeUntil > 0 && Date.now() < keepModeUntil;
  keepModeUntil = 0;
  return held;
}

/** Forget it: the send it belonged to is over, or the founder has left the editor. */
export function forgetTakenAsk(): void {
  lastTaken = null;
}

/**
 * The element on the composer, taken off it: what a send carries with the founder's words. The
 * chip goes with it, because the message it belonged to has been sent. Every send replaces what
 * the last one took, so a message without an element never inherits the one before it.
 */
export function takePendingAsk(): PendingAsk | null {
  const ask = hotEditStore.pendingAsk.get();
  if (!ask) {
    lastTaken = null;
    return null;
  }
  hotEditStore.pendingAsk.set(null);
  const taken =
    ask.screenshot && ask.screenshot.length > MAX_SCREENSHOT_CHARS
      ? (({ screenshot: _tooBig, ...rest }) => rest)(ask)
      : ask;
  lastTaken = { ask: taken, at: Date.now() };
  return taken;
}

/**
 * The element for the build request: the one still on the composer when the build goes out
 * without a send before it, otherwise the one the last send took. One build, one element - the
 * build after it, a crash repair or the next turn, is not about it.
 */
export function takeAskForBuild(): PendingAsk | null {
  if (hotEditStore.pendingAsk.get()) {
    const ask = takePendingAsk();
    lastTaken = null;
    return ask;
  }
  const taken = lastTaken;
  lastTaken = null;
  return taken && Date.now() - taken.at < SAME_TURN_MS ? taken.ask : null;
}

/**
 * The element the founder has put on the chat composer with Ask Ideoz (Prototype Editor UX plan,
 * step 5): a chip they can see and take off, and the context that rides with their message.
 */
export type PendingAsk = {
  askId: string;
  /** "Alert card, 3 of 5" - what the chip says. */
  label: string;
  context: ElementContext;
  /** A PNG data URL of the element, when the preview could draw one. */
  screenshot?: string;
};

/** A pending question, with what to do about each answer. */
export type SmartPromptState = {
  title: string;
  body: string;
  /** The answer that changes the prototype as a whole. */
  broad: { label: string; run: () => void };
  /** The answer that keeps the change where it was made. */
  narrow: { label: string; run: () => void };
};
